import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Space, Button, Tag, Input, Select, message, Modal, Form, Checkbox, Row, Col, Spin, Alert } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import { PlusOutlined, ReloadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import request from '../../../utils/request';
import { useAuth } from '../../../hooks/useAuth';
import { getProjectGroupList } from '../../../services/project';

const { Search } = Input;

type SubAccount = {
  id: number;
  username: string;
  email?: string;
  is_active: boolean;
  last_login?: string | null;
};

type ProjectItem = {
  id: number;
  project_code: string;
  name?: string;
  /** 来自列表接口 project_group_info 或 project_group；无归属时为 null */
  project_group_id: number | null;
  project_group_name?: string | null;
};

type ProjectGroupFilterValue = 'all' | 'ungrouped' | number;

function parseProjectGroupFilterValue(v: unknown): ProjectGroupFilterValue {
  if (v == null || v === 'all') return 'all';
  if (v === 'ungrouped') return 'ungrouped';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 'all';
}

/** 列表接口可能返回 project_group_info，也可能返回嵌套的 project_group 对象（或仅主键） */
function pickProjectGroupFromListItem(x: any): { id: number | null; name: string | null } {
  const info = x?.project_group_info;
  if (info && typeof info === 'object' && info.id != null) {
    const n = Number(info.id);
    return { id: Number.isFinite(n) ? n : null, name: (info.name as string) ?? null };
  }
  const grp = x?.project_group;
  if (grp && typeof grp === 'object' && grp.id != null) {
    const n = Number(grp.id);
    return { id: Number.isFinite(n) ? n : null, name: (grp.name as string) ?? null };
  }
  if (grp != null && grp !== '' && (typeof grp === 'number' || typeof grp === 'string')) {
    const n = Number(grp);
    return { id: Number.isFinite(n) ? n : null, name: null };
  }
  return { id: null, name: null };
}

const SubAccountManagement: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedUser, setSelectedUser] = useState<SubAccount | null>(null);

  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [checkedProjectIds, setCheckedProjectIds] = useState<number[]>([]);
  const [assignedProjectIds, setAssignedProjectIds] = useState<number[]>([]);
  const [assignedCounts, setAssignedCounts] = useState<Record<number, number>>({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [projectAssignedCounts, setProjectAssignedCounts] = useState<Record<number, number>>({});
  const [projectAssignedUsersMap, setProjectAssignedUsersMap] = useState<Record<number, SubAccount[]>>({});
  const [projUsersOpen, setProjUsersOpen] = useState(false);
  const [projUsersCurrent, setProjUsersCurrent] = useState<ProjectItem | null>(null);

  // 支持管理员从URL传入 customer_id 以复用组件
  const customerIdFromQuery = useMemo(() => {
    try {
      const search = new URLSearchParams(location.search);
      const cid = search.get('customer_id');
      return cid ? Number(cid) : undefined;
    } catch {
      return undefined;
    }
  }, [location.search]);
  const customerIdFromParams = useMemo(() => {
    const cid = (params as any)?.customerId;
    return cid ? Number(cid) : undefined;
  }, [params]);
  const overrideCustomerId = customerIdFromQuery || customerIdFromParams;

  /** 项目组 owner = 当前上下文的主账号用户 ID（管理员代客为 URL 客户；客户登录为主账号或其 parent） */
  const projectGroupOwnerId = useMemo(() => {
    if (overrideCustomerId != null && !Number.isNaN(overrideCustomerId)) return overrideCustomerId;
    if (user?.role === 'customer' && user.id) {
      const parent = (user as { profile?: { parent_customer_id?: number | null } }).profile?.parent_customer_id;
      return parent ?? user.id;
    }
    return undefined;
  }, [overrideCustomerId, user]);

  // 管理员场景：展示当前主账号信息
  const [mainAccount, setMainAccount] = useState<any | null>(null);
  const [mainAccountLoading, setMainAccountLoading] = useState(false);
  const isAdminContext = useMemo(() => location.pathname.startsWith('/dashboard/admin'), [location.pathname]);

  const fetchMainAccount = useMemo(() => async () => {
    if (!isAdminContext || !overrideCustomerId || Number.isNaN(overrideCustomerId)) {
      setMainAccount(null);
      return;
    }
    try {
      setMainAccountLoading(true);
      const res = await request.get(`/api/v1/users/${overrideCustomerId}/`);
      setMainAccount(res.data || null);
    } catch {
      setMainAccount(null);
    } finally {
      setMainAccountLoading(false);
    }
  }, [isAdminContext, overrideCustomerId]);

  // 管理员场景兼容：优先使用客户主账号接口，403 时回退到管理员 assignments 列表
  const getAssignedProjectIdsForUser = async (userId: number): Promise<number[]> => {
    try {
      const r = await request.get('/api/v1/permission-management/customer-assignments/', { params: { user_id: userId } });
      const ids: number[] = r.data?.assigned_project_ids ?? r.data?.project_ids ?? [];
      return Array.isArray(ids) ? ids : [];
    } catch (e: any) {
      try {
        const params: any = { user: userId, resource_type: 'project', is_active: true, page: 1, page_size: 1000, ordering: '-assigned_at' };
        const r2 = await request.get('/api/v1/permission-management/assignments/', { params });
        const results = r2.data?.results ?? r2.data ?? [];
        const ids = Array.isArray(results) ? results.map((x: any) => x?.resource_id).filter((v: any) => typeof v === 'number') : [];
        return ids;
      } catch {
        return [];
      }
    }
  };

  // 统计：项目总数 / 已分配项目数 / 未分配项目数
  const assignedProjectsTotal = useMemo(() => {
    if (!projects || projects.length === 0) return 0;
    return projects.reduce((acc, p) => acc + ((projectAssignedCounts[p.id] || 0) > 0 ? 1 : 0), 0);
  }, [projects, projectAssignedCounts]);
  const unassignedProjectsTotal = useMemo(() => {
    return (projects?.length || 0) - assignedProjectsTotal;
  }, [projects?.length, assignedProjectsTotal]);

  // 筛选：按是否已分配 + 所属项目组 + 按项目编码/名称搜索
  const [statusFilter, setStatusFilter] = useState<'all' | 'assigned' | 'unassigned'>('all');
  const [projectKeyword, setProjectKeyword] = useState('');
  const [groupFilter, setGroupFilter] = useState<ProjectGroupFilterValue>('all');
  const [allProjectGroups, setAllProjectGroups] = useState<Array<{ id: number; name: string }>>([]);
  const [projectGroupsLoading, setProjectGroupsLoading] = useState(false);

  const projectGroupFilterOptions = useMemo(() => {
    const byGroup = (allProjectGroups || [])
      .map((g) => ({ value: g.id, label: (g.name || '').trim() || `项目组 #${g.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'));
    return [
      { label: '全部项目组', value: 'all' as const },
      { label: '未分组', value: 'ungrouped' as const },
      ...byGroup,
    ];
  }, [allProjectGroups]);

  const visibleProjects = useMemo(() => {
    let list = projects || [];
    if (statusFilter !== 'all') {
      // 当选择了子账号时，按该子账号的分配集合过滤；未选择子账号则按“被任意子账号分配过”的集合过滤
      const assignedSet = selectedUser
        ? new Set(assignedProjectIds)
        : new Set((projects || []).filter((p) => (projectAssignedCounts[p.id] || 0) > 0).map((p) => p.id));
      list = list.filter((p) => (statusFilter === 'assigned' ? assignedSet.has(p.id) : !assignedSet.has(p.id)));
    }
    if (groupFilter === 'ungrouped') {
      list = list.filter((p) => p.project_group_id == null);
    } else if (groupFilter !== 'all') {
      const want = typeof groupFilter === 'number' ? groupFilter : Number(groupFilter);
      if (Number.isFinite(want)) {
        list = list.filter((p) => Number(p.project_group_id) === want);
      }
    }
    if (projectKeyword.trim()) {
      const q = projectKeyword.trim().toLowerCase();
      list = list.filter((p) => (p.project_code || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [projects, statusFilter, projectKeyword, groupFilter, assignedProjectIds, selectedUser, projectAssignedCounts]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdTarget, setResetPwdTarget] = useState<SubAccount | null>(null);
  const [resetPwdForm] = Form.useForm();
  const [resettingPwd, setResettingPwd] = useState(false);
  const [usernameCheckStatus, setUsernameCheckStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'invalid'>('idle');
  const [usernameCheckText, setUsernameCheckText] = useState('');
  const usernameCheckSeqRef = useRef(0);
  const createPassword = Form.useWatch('password', createForm);
  const createUsername = Form.useWatch('username', createForm);
  const createConfirmPassword = Form.useWatch('confirm_password', createForm);
  const hasCreateConfirmInput = String(createConfirmPassword || '').length > 0;
  const isCreatePasswordMatched = hasCreateConfirmInput && String(createPassword || '') === String(createConfirmPassword || '');

  useEffect(() => {
    if (!createModalOpen) {
      setUsernameCheckStatus('idle');
      setUsernameCheckText('');
      return;
    }
    const text = String(createUsername || '').trim();
    if (!text) {
      setUsernameCheckStatus('idle');
      setUsernameCheckText('');
      return;
    }
    if (text.length > 20) {
      setUsernameCheckStatus('invalid');
      setUsernameCheckText('用户名长度不能超过20位');
      return;
    }
    setUsernameCheckStatus('checking');
    setUsernameCheckText('正在检查用户名可用性...');
    const currentSeq = ++usernameCheckSeqRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const result = await request.get('/api/v1/users/username-availability/', { params: { username: text } });
        if (currentSeq !== usernameCheckSeqRef.current) return;
        if (result?.data?.available) {
          setUsernameCheckStatus('available');
          setUsernameCheckText(result?.data?.detail || '该用户名可用');
        } else {
          setUsernameCheckStatus('unavailable');
          setUsernameCheckText(result?.data?.detail || '用户名已存在，请更换后重试');
        }
      } catch (e: any) {
        if (currentSeq !== usernameCheckSeqRef.current) return;
        // 接口异常时不误判为“不可用”，避免出现“任何用户名都不可用”的误导
        setUsernameCheckStatus('idle');
        setUsernameCheckText('');
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [createModalOpen, createUsername]);

  // 获取子账号列表（支持管理员传入 customer_id：/api/v1/users/?sub_of=<id>，否则默认 me）
  const fetchSubAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (overrideCustomerId && !Number.isNaN(overrideCustomerId)) {
        params.append('sub_of', String(overrideCustomerId));
      } else {
        params.append('sub_of', 'me');
      }
      if (searchText) params.append('search', searchText);
      // 避免缓存，强制刷新
      params.append('_ts', String(Date.now()));
      const res = await request.get(`/api/v1/users/?${params.toString()}`);
      const raw = res.data?.results ?? res.data ?? [];
      const list: SubAccount[] = Array.isArray(raw) ? raw : [];
      setSubAccounts(list);
      // 预取每个子账号的已分配项目数量（小规模列表可承受）
      try {
        const pairs = await Promise.all(
          list.map(async (u) => {
            const ids = await getAssignedProjectIdsForUser(u.id);
            return { user: u, ids } as { user: SubAccount, ids: number[] };
          })
        );
        const userToCount: Record<number, number> = {};
        const projToUsers: Record<number, SubAccount[]> = {};
        pairs.forEach(({ user, ids }) => {
          userToCount[user.id] = Array.isArray(ids) ? ids.length : 0;
          (ids || []).forEach((pid) => {
            if (!projToUsers[pid]) projToUsers[pid] = [];
            projToUsers[pid].push(user);
          });
        });
        setAssignedCounts(userToCount);
        const projToCount: Record<number, number> = {};
        Object.keys(projToUsers).forEach((pid) => { projToCount[Number(pid)] = projToUsers[Number(pid)].length; });
        setProjectAssignedUsersMap(projToUsers);
        setProjectAssignedCounts(projToCount);
      } catch {}
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '获取子账号失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取主账号名下项目（支持管理员通过 customer_id 指定，默认当前用户；一次拉取足够多）
  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const reqParams: any = { page: 1, page_size: 1000, ordering: 'project_code' };
      if (overrideCustomerId && !Number.isNaN(overrideCustomerId)) {
        // 后端 ProjectViewSet 使用 DjangoFilterBackend，参数名应为 customer（外键字段名），非 customer_id
        reqParams.customer = overrideCustomerId;
      }
      const res = await request.get('/api/v1/project/projects/', { params: reqParams });
      const raw = res.data?.results ?? res.data ?? [];
      const list: ProjectItem[] = Array.isArray(raw)
        ? raw.map((x: any) => {
            const pg = pickProjectGroupFromListItem(x);
            return {
              id: x.id,
              project_code: x.project_code,
              name: x.name,
              project_group_id: pg.id,
              project_group_name: pg.name,
            };
          })
        : [];
      setProjects(list);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '获取项目列表失败');
    } finally {
      setProjectsLoading(false);
    }
  };

  // 主账号名下全部项目组（含当前无项目的组），用于筛选下拉
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (projectGroupOwnerId == null || Number.isNaN(Number(projectGroupOwnerId))) {
        setAllProjectGroups([]);
        setProjectGroupsLoading(false);
        return;
      }
      setProjectGroupsLoading(true);
      try {
        const res = await getProjectGroupList({
          page: 1,
          page_size: 1000,
          ordering: 'name',
          owner: projectGroupOwnerId,
        });
        if (cancelled) return;
        const raw = res.data?.results ?? res.data ?? [];
        setAllProjectGroups(
          Array.isArray(raw)
            ? raw.map((g: { id: number; name?: string }) => ({ id: g.id, name: g.name ?? '' }))
            : []
        );
      } catch (e: any) {
        if (!cancelled) {
          message.error(e?.response?.data?.detail || '获取项目组列表失败');
          setAllProjectGroups([]);
        }
      } finally {
        if (!cancelled) setProjectGroupsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectGroupOwnerId]);

  // 获取子账号已分配项目
  const fetchAssigned = async (userId: number) => {
    try {
      const ids: number[] = await getAssignedProjectIdsForUser(userId);
      setAssignedProjectIds(ids);
      setCheckedProjectIds(ids);
      // 同步更新数量缓存
      setAssignedCounts((prev) => ({ ...prev, [userId]: Array.isArray(ids) ? ids.length : 0 }));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '获取已分配项目失败');
    }
  };

  useEffect(() => {
    fetchSubAccounts();
    fetchProjects();
    fetchMainAccount();
  }, [overrideCustomerId]);

  // 当切换主账号时，清理当前选择与计数，避免短暂显示旧数据
  useEffect(() => {
    setSelectedUser(null);
    setAssignedProjectIds([]);
    setCheckedProjectIds([]);
    setAssignedCounts({});
    setProjectAssignedCounts({});
    setProjectAssignedUsersMap({});
    setGroupFilter('all');
    setAllProjectGroups([]);
  }, [overrideCustomerId]);

  // 选择子账号时刷新已分配
  useEffect(() => {
    if (selectedUser) {
      fetchAssigned(selectedUser.id);
    } else {
      setAssignedProjectIds([]);
      setCheckedProjectIds([]);
    }
  }, [selectedUser?.id]);

  const filteredSubAccounts = useMemo(() => {
    if (!searchText) return subAccounts;
    const q = searchText.toLowerCase();
    return subAccounts.filter((u) => u.username.toLowerCase().includes(q));
  }, [subAccounts, searchText]);

  const handleSaveAssignments = async () => {
    if (!selectedUser) {
      message.warning('请选择子账号');
      return;
    }
    const toAdd = checkedProjectIds.filter((id) => !assignedProjectIds.includes(id));
    const toRemove = assignedProjectIds.filter((id) => !checkedProjectIds.includes(id));
    if (toAdd.length === 0 && toRemove.length === 0) {
      message.info('无变更需要保存');
      return;
    }
    setSaving(true);
    try {
      if (toAdd.length > 0) {
        await request.post('/api/v1/permission-management/customer-assignments/', {
          user_id: selectedUser.id,
          project_ids: toAdd,
        });
      }
      if (toRemove.length > 0) {
        for (const pid of toRemove) {
          try {
            await request.delete(`/api/v1/permission-management/customer-assignments/${pid}/`, { params: { user_id: selectedUser.id, project_id: pid } });
          } catch {}
        }
      }
      message.success('已保存项目分配变更');
      await fetchAssigned(selectedUser.id);
      await fetchSubAccounts();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const subColumns = [
    { title: '用户名', dataIndex: 'username' },
    {
      title: '状态',
      dataIndex: 'is_active',
      render: (v: boolean) => (v ? <Tag color="green">已激活</Tag> : <Tag color="red">已禁用</Tag>)
    },
    {
      title: '已分配项目数',
      key: 'assigned_count',
      render: (_: any, row: SubAccount) => {
        const cnt = assignedCounts[row.id];
        return (
          <Button type="link" onClick={async () => { setSelectedUser(row); await fetchAssigned(row.id); setDetailOpen(true); }}>
            {typeof cnt === 'number' ? cnt : '—'}
          </Button>
        );
      }
    },
    ...(!isAdminContext ? [{
      title: '操作',
      key: 'actions',
      width: 230,
      render: (_: any, row: SubAccount) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() => {
              setResetPwdTarget(row);
              resetPwdForm.resetFields();
              setResetPwdOpen(true);
            }}
          >
            重置密码
          </Button>
          <Button
            size="small"
            danger={!!row.is_active}
            onClick={() => {
              Modal.confirm({
                title: row.is_active ? '确认禁用子账号？' : '确认启用子账号？',
                content: `账号：${row.username}`,
                okText: row.is_active ? '禁用' : '启用',
                cancelText: '取消',
                okButtonProps: { danger: !!row.is_active },
                onOk: async () => {
                  try {
                    await request.patch(`/api/v1/users/customer/sub-accounts/${row.id}/status/`, {
                      is_active: !row.is_active,
                    });
                    message.success(!row.is_active ? '已启用子账号' : '已禁用子账号');
                    if (selectedUser?.id === row.id) {
                      setSelectedUser({ ...selectedUser, is_active: !row.is_active });
                    }
                    await fetchSubAccounts();
                  } catch (e: any) {
                    message.error(e?.response?.data?.detail || '操作失败');
                  }
                },
              });
            }}
          >
            {row.is_active ? '禁用' : '启用'}
          </Button>
        </Space>
      )
    }] : []),
  ];

  const startCreate = () => {
    createForm.resetFields();
    setCreateModalOpen(true);
  };

  const submitCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      // 改用专用端点：POST /api/v1/users/customer/sub-accounts/
      const res = await request.post('/api/v1/users/customer/sub-accounts/', {
        username: values.username,
        password: values.password,
        // 其余角色/绑定由后端自动设置
      });
      message.success('子账号创建成功');
      setCreateModalOpen(false);
      // 清空搜索，避免因关键字过滤导致“看不到新用户”的错觉
      setSearchText('');
      await fetchSubAccounts();
      // 可选：高亮选择新创建的用户
      const created = res?.data;
      if (created && typeof created.id === 'number') {
        setSelectedUser({ id: created.id, username: created.username, email: created.email, is_active: created.is_active, last_login: created.last_login });
      }
    } catch (e: any) {
      const detail = String(e?.response?.data?.detail || '').trim();
      const lower = detail.toLowerCase();
      if (
        detail.includes('用户名已存在')
        || lower.includes('already exists')
        || lower.includes('duplicate entry')
        || lower.includes('users_user.username')
      ) {
        Modal.error({
          title: '创建失败',
          content: '用户名已存在，请更换后重试',
          okText: '确定',
        });
      } else {
        message.error(detail || '创建失败');
      }
    } finally {
      setCreating(false);
    }
  };

  const submitResetPassword = async () => {
    if (!resetPwdTarget) return;
    try {
      const values = await resetPwdForm.validateFields();
      setResettingPwd(true);
      await request.post(`/api/v1/users/customer/sub-accounts/${resetPwdTarget.id}/set-password/`, {
        password: values.password,
      });
      message.success('子账号密码已重置');
      setResetPwdOpen(false);
      setResetPwdTarget(null);
      resetPwdForm.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '重置失败');
    } finally {
      setResettingPwd(false);
    }
  };

  return (
    <div className="p-4">
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="使用说明"
        description={
          <div>
            左侧选择子账号，右侧执行项目分配设置；支持按“已分配/未分配”、所属项目组和系统编码/名称筛选；同一项目可分配给多个子账号。
          </div>
        }
      />
      <Row gutter={16}>
        <Col span={10}>
          {isAdminContext && (
            <Card size="small" style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>当前主账号</span>
                {mainAccountLoading && <span style={{ color: '#999', fontSize: 12 }}>加载中…</span>}
              </div>
              {mainAccount ? (
                <div style={{ marginTop: 6, fontSize: 13 }}>
                  <div>用户名：<Tag color="blue">{mainAccount.username}</Tag></div>
                  <div style={{ color: '#666', marginTop: 4 }}>客户编码：{mainAccount.customer_code || mainAccount.profile?.customer_code || '—'}</div>
                  {mainAccount.profile?.company_name && (
                    <div style={{ color: '#666', marginTop: 4 }}>公司：{mainAccount.profile.company_name}</div>
                  )}
                  <div style={{ color: '#666', marginTop: 4 }}>ID：{mainAccount.id}</div>
                </div>
              ) : (
                <div style={{ marginTop: 6, color: '#999', fontSize: 13 }}>未获取到主账号信息</div>
              )}
            </Card>
          )}
          <Card title="子账号列表" extra={
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchSubAccounts}>刷新</Button>
              <Button onClick={() => setSelectedUser(null)} disabled={!selectedUser}>取消选择</Button>
              {!isAdminContext && (
                <Button type="primary" icon={<PlusOutlined />} onClick={startCreate}>新建子账号</Button>
              )}
            </Space>
          }>
            <div style={{ marginBottom: 12 }}>
              <Search placeholder="搜索用户名" allowClear onSearch={setSearchText} onChange={(e) => setSearchText(e.target.value)} />
            </div>
            <Table
              rowKey="id"
              loading={loading}
              columns={subColumns as any}
              dataSource={Array.isArray(filteredSubAccounts) ? filteredSubAccounts : []}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedUser ? [selectedUser.id] : [],
                onChange: (keys, rows) => setSelectedUser(rows[0] || null),
              }}
            />
          </Card>
        </Col>
        <Col span={14}>
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <span>项目分配</span>
                <span style={{ color: '#666' }}>
                  项目总数：{projects.length} ｜ 已分配：{assignedProjectsTotal} ｜ 未分配：{unassignedProjectsTotal}
                </span>
              </div>
            }
          >
            {projectsLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}><Spin /></div>
            ) : (
              <div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 16, color: '#666' }}>
                  <div>
                    当前子账号：{selectedUser ? (<Tag color="blue">{selectedUser.username}</Tag>) : (<span style={{ color: '#999' }}>未选择</span>)}
                  </div>
                  <div>
                    已分配项目：{assignedProjectIds.length} 个
                  </div>
                </div>
                <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <Select
                    style={{ width: 140 }}
                    value={statusFilter}
                    onChange={(v) => setStatusFilter(v)}
                    options={[
                      { label: '全部状态', value: 'all' },
                      { label: '已分配', value: 'assigned' },
                      { label: '未分配', value: 'unassigned' },
                    ]}
                  />
                  <Select
                    style={{ minWidth: 160, maxWidth: 240 }}
                    placeholder="所属项目组"
                    value={groupFilter}
                    onChange={(v) => setGroupFilter(parseProjectGroupFilterValue(v))}
                    options={projectGroupFilterOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                    loading={projectGroupsLoading}
                  />
                  <Search
                    style={{ maxWidth: 280 }}
                    placeholder="按系统编码或名称搜索"
                    allowClear
                    onSearch={(v) => setProjectKeyword(v)}
                    onChange={(e) => setProjectKeyword(e.target.value)}
                  />
                </div>
                <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 12 }}>
                  <Row gutter={[12, 8]}>
                    {visibleProjects.map((p) => {
                      const label = `${p.project_code}${p.name ? ' - ' + p.name : ''}`;
                      const checked = checkedProjectIds.includes(p.id);
                      const assigned = assignedProjectIds.includes(p.id);
                      const assignedUserCnt = projectAssignedCounts[p.id] || 0;
                      return (
                        <Col span={12} key={p.id}>
                          <Checkbox
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? Array.from(new Set([...checkedProjectIds, p.id]))
                                : checkedProjectIds.filter((id) => id !== p.id);
                              setCheckedProjectIds(next);
                            }}
                          >
                            <span>{label}</span>
                            {assignedUserCnt > 0 && (
                              <span style={{ marginLeft: 8, color: '#999' }}>
                                已分配至<Button type="link" size="small" onClick={() => { setProjUsersCurrent(p); setProjUsersOpen(true); }}>{assignedUserCnt}</Button>个子账号
                              </span>
                            )}
                          </Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
                {!isAdminContext && (
                  <Space style={{ marginTop: 12 }}>
                    <Button type="primary" icon={<CheckOutlined />} onClick={handleSaveAssignments} disabled={!selectedUser} loading={saving}>保存</Button>
                  </Space>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="新建子账号"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={submitCreate}
        confirmLoading={creating}
        cancelText="取消"
        okText="创建子账号"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="username"
            label="用户名（子账户必须有用户名，且只能以用户名登录系统）"
            validateStatus={
              usernameCheckStatus === 'available'
                ? 'success'
                : usernameCheckStatus === 'checking'
                  ? 'validating'
                  : usernameCheckStatus === 'unavailable' || usernameCheckStatus === 'invalid'
                    ? 'error'
                    : undefined
            }
            help={
              usernameCheckText
                ? (usernameCheckStatus === 'available'
                  ? <span style={{ color: '#16a34a' }}>{usernameCheckText}</span>
                  : usernameCheckText)
                : undefined
            }
            rules={[
              { required: true, message: '请输入用户名' },
              { max: 20, message: '用户名长度不能超过20位' },
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item name="password" label="初始密码" rules={[{ required: true, message: '请输入初始密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password placeholder="请输入初始密码" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认密码"
            dependencies={['password']}
            validateStatus={hasCreateConfirmInput ? (isCreatePasswordMatched ? 'success' : 'error') : undefined}
            help={
              hasCreateConfirmInput
                ? (isCreatePasswordMatched
                  ? <span style={{ color: '#16a34a' }}>两次输入的密码一致</span>
                  : '两次输入的密码不一致')
                : undefined
            }
            rules={[
              { required: true, message: '请再次输入初始密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const pwd = getFieldValue('password');
                  if (!value || value === pwd) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入初始密码" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={resetPwdTarget ? `重置密码：${resetPwdTarget.username}` : '重置密码'}
        open={resetPwdOpen}
        onCancel={() => {
          setResetPwdOpen(false);
          setResetPwdTarget(null);
          resetPwdForm.resetFields();
        }}
        onOk={submitResetPassword}
        okText="确认重置"
        cancelText="取消"
        confirmLoading={resettingPwd}
        destroyOnClose
      >
        <Form form={resetPwdForm} layout="vertical">
          <Form.Item
            label="新密码"
            name="password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            label="确认密码"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={projUsersCurrent ? `分配此项目的子账号 - ${projUsersCurrent.project_code}${projUsersCurrent.name ? ' - ' + projUsersCurrent.name : ''}` : '分配此项目的子账号'}
        open={projUsersOpen}
        onCancel={() => setProjUsersOpen(false)}
        footer={null}
        destroyOnClose
      >
        {projUsersCurrent ? (
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {(projectAssignedUsersMap[projUsersCurrent.id] || []).length === 0 ? (
              <div style={{ color: '#999' }}>暂无子账号被分配此项目</div>
            ) : (
              (projectAssignedUsersMap[projUsersCurrent.id] || []).map((u) => (
                <div key={u.id} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                  <span>{u.username}</span>
                  <span style={{ marginLeft: 8, color: '#999' }}>{u.email || ''}</span>
                </div>
              ))
            )}
          </div>
        ) : null}
      </Modal>
      <Modal
        title={selectedUser ? `已分配项目（${assignedProjectIds.length}） - ${selectedUser.username}` : '已分配项目'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        destroyOnClose
      >
        {assignedProjectIds.length === 0 ? (
          <div style={{ color: '#999' }}>暂无已分配项目</div>
        ) : (
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {assignedProjectIds.map((pid) => {
              const p = projects.find((x) => x.id === pid);
              const label = p ? `${p.project_code}${p.name ? ' - ' + p.name : ''}` : `ID ${pid}`;
              return <div key={pid} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>{label}</div>;
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SubAccountManagement;


